import React, { useContext, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { Button, Col, Input, Label, Row, Table } from "reactstrap";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import Seccion from "../../Seccion";

const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const fmt = (v) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(v || 0);
const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

function AbonosProveedoresCreate() {
  const { user } = useContext(AuthContext);
  const URL_PROVEEDORES = process.env.REACT_APP_URL_PROVEEDORES;
  const URL_BANCOS = process.env.REACT_APP_URL_BANCOS;
  const URL_NOTAS = process.env.REACT_APP_URL_NOTAS_CXP;
  const URL_ABONOS = process.env.REACT_APP_URL_ABONOS_PROVEEDORES;
  const history = useHistory();

  const [proveedores, setProveedores] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [proveedor, setProveedor] = useState("");
  const [banco, setBanco] = useState("");
  const [fecha, setFecha] = useState(moment().format("YYYY-MM-DD"));
  const [concepto, setConcepto] = useState("Abono");
  const [observaciones, setObservaciones] = useState("");
  const [compras, setCompras] = useState([]);
  const [monto, setMonto] = useState("");
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    axios.get(URL_PROVEEDORES, { headers: headers() }).then((r) => setProveedores(r.data.filter((p) => p.is_active === "Si"))).catch(console.log);
    axios.get(URL_BANCOS, { headers: headers() }).then((r) => setBancos(r.data)).catch(console.log);
  }, [URL_PROVEEDORES, URL_BANCOS]);

  useEffect(() => {
    setCompras([]);
    setMonto("");
    if (!proveedor) return;
    axios.get(`${URL_NOTAS}Pendientes/${proveedor}`, { headers: headers() })
      .then((r) => setCompras(r.data.map((n) => ({
        id: n._id, numero: n.idNotasCxP, fecha: n.fecha, oc: n.ordenesCompra?.[0]?.idOrdenCompra, factura: n.compras?.[0]?.factura,
        total: n.total_costo, saldo: r2(n.saldo), aplicar: "",
      }))))
      .catch(console.log);
  }, [proveedor, URL_NOTAS]);

  const total = useMemo(() => r2(compras.reduce((t, c) => t + (parseFloat(c.aplicar) || 0), 0)), [compras]);
  const saldoTotal = useMemo(() => r2(compras.reduce((t, c) => t + c.saldo, 0)), [compras]);
  const excedido = compras.some((c) => (parseFloat(c.aplicar) || 0) > c.saldo);

  const cambiar = (id, valor) => setCompras((l) => l.map((c) => (c.id === id ? { ...c, aplicar: valor } : c)));

  // Reparte un monto entre las compras, de la mas antigua a la mas nueva
  const repartir = () => {
    let resto = parseFloat(monto) || 0;
    const repartidas = compras.map((c) => {
      const a = r2(Math.min(c.saldo, resto));
      resto = r2(resto - a);
      return { ...c, aplicar: a > 0 ? a : "" };
    });
    setCompras(repartidas);
  };

  const guardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      await axios.post(`${URL_ABONOS}/aplicar`, {
        fecha, proveedores: proveedor, bancos: banco, concepto, observaciones: observaciones.trim(),
        aplicaciones: compras.filter((c) => parseFloat(c.aplicar) > 0).map((c) => ({ notasCxP: c.id, importe: parseFloat(c.aplicar) })),
      }, { headers: headers() });
      await Swal.fire("Pago guardado", `Se aplicaron ${fmt(total)} a las compras seleccionadas`, "success");
      history.push("/ListadoAbonosProveedores");
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se guardo", text: error.response?.data?.message || "Intenta de nuevo" });
      setGuardando(false);
    }
  };

  if (!user.pagoProveedoresCreate) return <Header />;

  return (
    <>
      <Header />
      <div className="card container col-12">
        <div className="page-header">
          <div><h3 className="page-title">Pago a proveedor</h3><p className="page-subtitle">Aplica el pago a una o varias compras</p></div>
        </div>
        <form onSubmit={guardar}>
          <Seccion titulo="Datos del pago" icono="fas fa-money-check-alt">
            <Row>
              <Col md={4}>
                <Label>Proveedor</Label>
                <Input type="select" value={proveedor} required onChange={(e) => setProveedor(e.target.value)}>
                  <option value="">Selecciona</option>
                  {proveedores.map((p) => <option key={p._id} value={p._id}>{p.nombre_comercial}</option>)}
                </Input>
              </Col>
              <Col md={2}><Label>Fecha</Label><Input type="date" value={fecha} required onChange={(e) => setFecha(e.target.value)} /></Col>
              <Col md={3}>
                <Label>Banco</Label>
                <Input type="select" value={banco} required onChange={(e) => setBanco(e.target.value)}>
                  <option value="">Selecciona</option>
                  {bancos.map((b) => <option key={b._id} value={b._id}>{b.banco} {b.cuenta}</option>)}
                </Input>
              </Col>
              <Col md={3}>
                <Label>Concepto</Label>
                <Input type="select" value={concepto} onChange={(e) => setConcepto(e.target.value)}>
                  <option>Abono</option><option>Anticipo</option><option>Liquidacion</option>
                </Input>
              </Col>
            </Row>
            <Row><Col md={12}><Label>Observaciones</Label><Input value={observaciones} onChange={(e) => setObservaciones(e.target.value)} /></Col></Row>
          </Seccion>

          <Seccion titulo="Compras por pagar" icono="fas fa-file-invoice-dollar" ayuda={proveedor ? `Saldo del proveedor: ${fmt(saldoTotal)}` : "Elige un proveedor"}>
            {compras.length > 0 ? (
              <>
                <div className="table-toolbar">
                  <span>Repartir un monto entre las mas antiguas</span>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Input bsSize="sm" type="number" min="0" step="any" placeholder="Monto" value={monto} onChange={(e) => setMonto(e.target.value)} style={{ width: 130 }} />
                    <Button type="button" size="sm" color="secondary" onClick={repartir}>Repartir</Button>
                  </div>
                </div>
                <Table size="sm" responsive>
                  <thead><tr><th>Compra</th><th>Fecha</th><th>OC</th><th>Factura</th><th className="text-right">Total</th><th className="text-right">Saldo</th><th style={{ width: 150 }}>Aplicar</th><th /></tr></thead>
                  <tbody>
                    {compras.map((c) => (
                      <tr key={c.id}>
                        <td>{c.numero}</td><td>{c.fecha}</td><td>{c.oc}</td><td>{c.factura}</td>
                        <td className="text-right td-money">{fmt(c.total)}</td>
                        <td className="text-right td-money">{fmt(c.saldo)}</td>
                        <td><Input bsSize="sm" type="number" min="0" step="any" max={c.saldo} value={c.aplicar} invalid={(parseFloat(c.aplicar) || 0) > c.saldo} onChange={(e) => cambiar(c.id, e.target.value)} /></td>
                        <td className="text-right"><Button type="button" size="sm" color="secondary" onClick={() => cambiar(c.id, c.saldo)}>Liquidar</Button></td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </>
            ) : <p className="rp-vacio">{proveedor ? "Este proveedor no tiene compras por pagar" : "Sin proveedor seleccionado"}</p>}
          </Seccion>

          <div className="form-totales" style={{ marginBottom: 12 }}>
            <div className="principal"><span>Total del pago</span><strong>{fmt(total)}</strong></div>
            <div><span>Saldo despues del pago</span><strong>{fmt(saldoTotal - total)}</strong></div>
          </div>
          <div className="form-acciones">
            <Button type="button" color="secondary" onClick={() => history.push("/ListadoAbonosProveedores")}>Regresar</Button>
            <Button type="submit" color="primary" disabled={guardando || !total || excedido || !banco || !proveedor}>{guardando ? "Guardando..." : "Guardar pago"}</Button>
          </div>
        </form>
      </div>
    </>
  );
}

export default AbonosProveedoresCreate;
